<template>
  <div class="input-radio" :class="{ small }">
    <input
      ref="checkbox"
      type="radio"
      :id="iuid"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      :tabindex="tabindex"
      @click="handleChange($event)"
      @change="handleChange($event)"
      @blur="handleBlur($event)"
    />
    <label
      :for="iuid"
      tabindex="-1"
      :title="tooltip"
      v-html="label"
      @click="handleChange($event)"
    />
    <div class="clickarea" @click="toggle"/>
  </div>
</template>

<script>
  let uid = 0;

  export default {
    name: 'Radio',
    components: {
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: [Boolean, String, Number],
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      checked: {
        type: [Boolean, String, Number],
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      tabindex: {
        type: Number,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      small: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        iuid: `input-radio-${uid}`,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      invalid() {
        return (this.vuelidate && this.vuelidate.$dirty && this.vuelidate.$invalid);
      },
      isChecked() {
        if (Array.isArray(this.checked) && this.value !== null && this.value !== undefined) {
          return this.checked.includes(this.value);
        }
        if (this.value !== null && this.value !== undefined) {
          return this.checked === this.value;
        }
        return this.checked;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      toggle() {
        const { checkbox } = this.$refs;
        checkbox.checked = !checkbox.checked;
        this.update();
      },
      update() {
        const { checkbox } = this.$refs;
        let value = checkbox.checked;

        if (Array.isArray(this.checked) && this.value !== null && this.value !== undefined) {
          value = this.checked.slice();
          const idx = value.indexOf(this.value);
          if (checkbox.checked && idx === -1) {
            value.push(this.value);
          }
          if (!checkbox.checked && idx !== -1) {
            value.splice(idx, 1);
          }
        } else if (this.value !== null && this.value !== undefined) {
          value = checkbox.checked ? this.value : undefined;
        }

        this.$emit('update:checked', value);
        this.$emit('input', value, this.value);
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleChange(evt) {
        evt.stopPropagation();
        if (evt.type !== 'click') {
          this.update();
        }
      },
      handleBlur(evt) {
        if (this.vuelidate) {
          this.vuelidate.$touch();
        }
        this.$emit('blur', evt);
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-radio {
  position: relative;
  display: inline-block;
  height: 22px;
  min-width: 16px;

  input[type=radio] {
    position: absolute;
    z-index: -1;
    opacity: 0;
    outline: 0;

    & + label {
      display: inline-block;
      position: relative;
      padding-left: 24px;
      -webkit-tap-highlight-color: transparent;
      @include font-body-medium;
      color: $color-black-alpha-80;
      white-space: nowrap;
      user-select: none;
      cursor: pointer;
      outline: 0;

      &:empty {
        min-height: 15px;
        padding-Left: 0;
      }
    }

    /* Box */
    & + label:before {
      content: '';
      position: absolute;
      display: inline-block;
      left: 0;
      top: 4px;
      width: 15px;
      height: 15px;
      border-width: 1px;
      border-style: solid;
      border-color: $color-black-solid-100;
      border-radius: 100%;
      background-color: $color-white-solid-10;
      will-change: background-color, border-color;
      transition: background-color $default-transition, border-color $default-transition;
      z-index: 1;
    }

    &:hover {
      &:not([disabled]) + label:before {
        border-color: currentColor;
      }
    }

    &:focus {
      & + label:before {
        // animation: blip $default-transition;
      }
    }

    /* Checkmark */
    & + label:after {
      content: '';
      position: absolute;
      display: inline-block;
      left: 0;
      top: 4px;
      width: 15px;
      height: 15px;
      border-width: 1px;
      border-style: solid;
      border-color: $color-black-solid-100;
      border-radius: 100%;
      transform: scale(0, 0);
      will-change: transform;
      background-color: $color-black-solid-100;
      transition: transform $default-transition;
      z-index: 2;
    }

    &:checked + label:after {
      transform: scale(0.6, 0.6);
    }
  }

  & > .clickarea {
    position: absolute;
    left: -6px;
    top: -1.5px;
    width: 27px;
    height: 27px;
    background: red;
    border-radius: 100%;
    opacity: 0;
    cursor: pointer;
  }

  &.changed {
    input[type=radio] {
      & + label:before {
        box-shadow: 0 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 5px 3px rgba(214, 16, 16, 0.8);
      }
    }
  }
}
</style>
